import { createSelector } from 'reselect';
import { getPersonnelRoles } from 'shared/helpers';
import { orderBy, uniqBy, omit } from 'lodash';


const getEventStaff = (state) => state.staff.tcEventStaff;
const getAvailableTcStaff = (state) => state.staff.availableTcStaff;
const getInvitedEventStaff = (state) => state.staff.invitedEventStaff;
const getTcNonPersonnelStaff = (state) => state.staff.tcNonPersonnelStaff;
const getTcEventNonPersonnelStaff = (state) => state.staff.tcEventNonPersonnelStaff;


export const getNonPersonnelStaff = createSelector(
  [getTcNonPersonnelStaff],
  (tcNonPersonnelStaff) => orderBy(tcNonPersonnelStaff, ['lastName', 'firstName'])
);

export const getTechnicalCoordinator = createSelector(
  [getTcEventNonPersonnelStaff],
  (tcEventNonPersonnelStaff) => {
    const personnelRoles = getPersonnelRoles();
    const techCoordinator = tcEventNonPersonnelStaff
      .find((staff) => staff.roleName === personnelRoles.TECHNICAL_COORDINATOR);
    return techCoordinator;
  }
);

export const getTdCoordinator = createSelector(
  [getEventStaff],
  (eventStaff) => {
    const personnelRoles = getPersonnelRoles();
    const tdCoordinator = eventStaff
      .find((staff) => staff.roleName === personnelRoles.TEST_DAY_COORDINATOR);

    return tdCoordinator ? { ...tdCoordinator, isTDC: true } : tdCoordinator;
  }
);

export const getAssignedStaff = createSelector(
  [getEventStaff, getTdCoordinator],
  (eventStaff, tdCoordinator) => {
    const personnelRoles = getPersonnelRoles();
    const filteredStaff = eventStaff
      .filter((staff) => staff.roleId && staff.roleName !== personnelRoles.TEST_DAY_COORDINATOR)
      .map((staff) => (
        {
          ...staff,
          isTDC: tdCoordinator ? tdCoordinator.personnelId === staff.personnelId : false,
        }
      ));

    return filteredStaff || []; //no TDC role
  }
);

export const getAvailableToAssign = createSelector(
  [getAssignedStaff, getAvailableTcStaff],
  (assignedStaff, availableTcStaff) => availableTcStaff.filter((avStaff) => !assignedStaff.some((staff) => staff.personnelId === avStaff.personnelId))
);


export const getRovingProctors = createSelector(
  [getAssignedStaff],
  (assignedStaff) => {
    const personnelRoles = getPersonnelRoles();
    return assignedStaff
      .filter((staff) => staff.roleName === personnelRoles.PROCTOR_ROVING);
  }
);

export const getTechCoordinator = createSelector(
  [getAssignedStaff],
  (assignedStaff) => {
    const personnelRoles = getPersonnelRoles();
    return assignedStaff
      .find((staff) => staff.roleName === personnelRoles.TECH_COORDINATOR);
  }
);

export const getAssignableStaff = createSelector(
  [getAssignedStaff, getAvailableToAssign, getTdCoordinator],
  (assignedStaff, availableToAssign, tdCoordinator) => {
    const listOfStaff = assignedStaff.concat(availableToAssign);
    if (tdCoordinator) {
      const tdcIdx = listOfStaff.findIndex((staff) => staff.personnelId === tdCoordinator.personnelId);
      if (tdcIdx > -1) {
        listOfStaff[tdcIdx].isTDC = true;
        listOfStaff[tdcIdx].testCenterEventPersonnelId = listOfStaff[tdcIdx].testCenterEventPersonnelId || tdCoordinator.testCenterEventPersonnelId;
      } else {
        listOfStaff.push({
          ...omit(tdCoordinator, ['roleId', 'roleName', 'roleShortName']),
        });
      }
    }

    return orderBy(uniqBy(listOfStaff, 'personnelId'), ['lastName', 'firstName']);
  }
);

export const getEventStaffGroups = createSelector(
  [getAvailableToAssign, getAssignedStaff, getInvitedEventStaff, getAssignableStaff, getTdCoordinator, getRovingProctors, getTechCoordinator,],
  (availableToAssign, assignedStaff, invitedEventStaff, assignableStaff, tdCoordinator, rovingProctors, techCoordinator,) => ({
    availableToAssign: orderBy(availableToAssign, ['lastName', 'firstName']),
    assignedStaff: orderBy(assignedStaff, ['lastName', 'firstName']),
    invitedEventStaff: orderBy(invitedEventStaff, ['lastName', 'firstName']),
    assignableStaff,
    tdCoordinator,
    rovingProctors,
    techCoordinator,
  })
);


export const getStaffByRoom = createSelector(
  [getAssignedStaff],
  (assignedStaff) => {
    const rooms = {};

    assignedStaff.forEach((staff) => {
      if (staff.roomId) {
        rooms[staff.roomId] = rooms[staff.roomId] || [];
        rooms[staff.roomId].push(staff);
      }
    });
    return rooms;
  }
);

export const setEventStaffCounts = createSelector(
  [getEventStaff, getTcEventNonPersonnelStaff],
  (staff, nonPersonnelStaff) => {
    const staffCounts = {
      available: 0,
      roomSupervisors: 0,
      proctors: 0,
      rovingProctors: 0,
      techCoordinator: 0,
    };

    const personnelRoles = getPersonnelRoles();

    staff.forEach((pers) => {
      if (!pers.roleName) {
        return;
      }
      if (pers.roleName === personnelRoles.ROOM_SUPERVISOR) {
        staffCounts.roomSupervisors += 1;
      } else if (pers.roleName === personnelRoles.PROCTOR) {
        staffCounts.proctors += 1;
      } else if (pers.roleName === personnelRoles.PROCTOR_ROVING) {
        staffCounts.rovingProctors += 1;
      } else if (pers.roleName === personnelRoles.TECH_COORDINATOR) {
        staffCounts.techCoordinator += 1;
      }
    });

    nonPersonnelStaff.forEach((pers) => {
      if (pers.roleName === personnelRoles.TECHNICAL_COORDINATOR) {
        staffCounts.techCoordinator += 1;
      }
    });

    return staffCounts;
  }
);
